import { useEffect } from "react";

import { useSecureMode } from "@hooks/use-secure-mode";

import { isExternalHref } from "./external-link.helpers";

const isSanctioned = (anchor) =>
  anchor.hasAttribute("download") || "externalLink" in anchor.dataset;

/**
 * Default-deny backstop for secure mode: cancels any navigation that leaves the
 * app, including anchors rendered by code that never learned about the setting.
 * Presentation still belongs to the surface — a link the user can see but not
 * follow reads as broken.
 */
export const useExternalNavigationGuard = () => {
  const secureMode = useSecureMode();

  useEffect(() => {
    if (!secureMode) {
      return;
    }

    const blockAnchor = (event) => {
      const anchor = event.target?.closest?.("a[href]");

      if (!anchor || isSanctioned(anchor) || !isExternalHref(anchor.href)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    const originalOpen = globalThis.open;

    document.addEventListener("click", blockAnchor, true);
    // A blocked window comes back unreachable, which is what callers of
    // window.open already handle a popup blocker returning.
    globalThis.open = (url, ...rest) =>
      isExternalHref(url ?? "")
        ? undefined
        : originalOpen.call(globalThis, url, ...rest);

    return () => {
      document.removeEventListener("click", blockAnchor, true);
      globalThis.open = originalOpen;
    };
  }, [secureMode]);
};
